import { useEffect, useRef, useState } from 'react';

const BORDERS_OFFSET = 2; // offset for borders via outline
const DEFAULT_PLACEHOLDER_HEIGHT = 25; // default height when container is loading

const getHeights = (
  containerRef: React.RefObject<HTMLElement>,
  visibleRows: number,
  maxVisibleRows: number,
) => {
  const container = containerRef.current;

  const firstTag = container!.firstElementChild as HTMLElement | null;

  const tagHeight = firstTag?.offsetHeight ?? 0;

  const computedStyle = getComputedStyle(container!);
  const rowGap = parseFloat(computedStyle.rowGap || computedStyle.gap || '0');

  return {
    visibleRowsHeight:
      tagHeight * visibleRows + rowGap * (visibleRows - 1) + BORDERS_OFFSET,
    maxVisibleRowsHeight:
      tagHeight * maxVisibleRows +
      rowGap * (maxVisibleRows - 1) +
      BORDERS_OFFSET,
  };
};

export const useRowsHeights = (
  containerRef: React.RefObject<HTMLElement>,
  visibleRows: number,
  maxVisibleRows = 10,
) => {
  const resizeRef = useRef<ResizeObserver | null>(null);

  const [heights, setHeights] = useState({
    visibleRowsHeight: DEFAULT_PLACEHOLDER_HEIGHT,
    maxVisibleRowsHeight: DEFAULT_PLACEHOLDER_HEIGHT,
  });

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const handleResize = () => {
      setHeights(getHeights(containerRef, visibleRows, maxVisibleRows));
    };

    resizeRef.current = new ResizeObserver(handleResize);

    resizeRef.current.observe(containerRef.current);

    return () => {
      resizeRef.current?.disconnect();
    };
  }, [containerRef, maxVisibleRows, visibleRows]);

  const isOverflow =
    Number(containerRef?.current?.scrollHeight) > heights.visibleRowsHeight ||
    false;

  return { ...heights, isOverflow };
};
